import { FormSection } from 'framework/ui/layout';
import { useEffect } from 'react';
import { DateInput, useFormContext } from 'framework/form';
import { Button } from 'framework/ui/mui-builder';
import { ButtonWithConfirmation } from 'framework/ui/ui-builder';
import { CUSTOMER_REPORT_STATUS, useNewProjectUrl } from 'domain/shared/newProject';
import useCustomerReportService from '../useCustomerReportService';
import { useCustomerReportPlaceholders } from '../hooks/useCustomerReportPlaceholders';
import { Box, Typography } from '@mui/material';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import { filterReportData } from '../utills';
import { Grid2 as Grid } from '@mui/material';
import { MarkdownEditorWithPreview } from 'domain/shared/core/input';

const DavinciReportForm = ({
  projectId,
  reportFrequency,
  projectStartDate,
}: {
  projectId: string;
  reportFrequency?: string;
  projectStartDate?: string | null;
}) => {
  const { data = {}, onChangeCallback, isLoadingArtifact, setIsLoadingArtifact } = useFormContext();
  const { cancelCustomerReport, contentReportSearch } = useCustomerReportService();
  const { placeholderValues, updatePlaceholderValues } = useCustomerReportPlaceholders(projectId);
  const { getCustomerReportUrl } = useNewProjectUrl();
  const { addMessage } = useMessageService();

  const handleUpdateContent = async () => {
    try {
      setIsLoadingArtifact?.(true);
      const response = await contentReportSearch(projectId, true, {});
      const filtered = filterReportData(response, reportFrequency, projectStartDate);
      const payload = {
        businessOutcomes: filtered.businessOutcomes,
        milestones: filtered.milestones,
        risks: filtered.risks,
        changeRequests: filtered.changeRequests,
      };
      onChangeCallback?.(payload);
      await updatePlaceholderValues({ ...data, ...payload });
    } catch (error) {
      addMessage(new ErrorMessage(`Error fetching content:${error}`));
    } finally {
      setIsLoadingArtifact?.(false);
    }
  };

  useEffect(() => {
    updatePlaceholderValues(data);
  }, [projectId, data.template]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <FormSection>
        <Grid
          container
          spacing={2}
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Grid container spacing={2} direction="row">
            <Grid size={6}>
              <DateInput source="periodStart" label="Period from" minDate={data.lastReportPeriodEnd} />
            </Grid>
            <Grid size={6}>
              <DateInput source="periodEnd" label="Period to" minDate={data.periodStart} />
            </Grid>
          </Grid>
          <Grid sx={{ display: 'flex', justifyContent: 'flex-end', flexDirection: 'column', alignItems: 'flex-end' }}>
            {data.status === CUSTOMER_REPORT_STATUS.REJECTED && (
              <Typography color="error" pb={1}>
                Rejected
              </Typography>
            )}
            {(data.status === CUSTOMER_REPORT_STATUS.DRAFT || data.status === CUSTOMER_REPORT_STATUS.REJECTED) && (
              <Box sx={{ gap: 1, display: 'flex', flexDirection: 'row' }}>
                <Button variant="outlined" onClick={handleUpdateContent} loading={isLoadingArtifact}>
                  Refresh data
                </Button>

                <ButtonWithConfirmation
                  variant="contained"
                  color="error"
                  submitMethod={() => cancelCustomerReport(data.id, {})}
                  afterSubmit={{
                    redirectUrl: getCustomerReportUrl(projectId),
                    successMessage: 'Customer report has been successfully canceled.',
                    errorMessage: "Can't cancel Customer report.",
                  }}
                  displayMessage="Do you really want to cancel this report?"
                  modalConfirmButtonText="Cancel report"
                >
                  Cancel report
                </ButtonWithConfirmation>
              </Box>
            )}
          </Grid>
        </Grid>
        {data.status === CUSTOMER_REPORT_STATUS.REJECTED && (
          <Typography color="error" py={2}>
            {data.rejectionReason}
          </Typography>
        )}
        <MarkdownEditorWithPreview
          defaultTemplateFileName={'customer-report-template.md'}
          source="template"
          label=""
          placeholder=""
          templatePlaceholders={placeholderValues}
          disabled={isLoadingArtifact}
        />
      </FormSection>
    </>
  );
};

export default DavinciReportForm;
